/**
 * A one-time secret, shown once and never again.
 *
 * Used by every flow that hands over a credential: creating staff, resetting a
 * credential, provisioning a school's first admin, and confirming the password an
 * admin set for a new student.
 * One component rather than four, because the copy has to say the same thing
 * each time — a staff member who is told "this is shown once" on one screen and
 * not on another will assume they can come back for it.
 *
 * There is no "show again". The server generated it, hashed it, and kept no
 * plaintext; if this box is dismissed the only path forward is another reset.
 */
import { useState } from 'react';
import { Button } from '@/components/ds';
import { fontPx } from '@/lib/fontSize';

export interface SecretRevealProps {
  title: string;
  /** What the person signs in with — an email, or a roll number. */
  identifier?: string | null;
  secret: string;
  onDismiss: () => void;
}

export function SecretReveal({ title, identifier, secret, onDismiss }: SecretRevealProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = identifier ? `${identifier}\n${secret}` : secret;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused (insecure origin, permissions). The
      // secret is on screen and selectable, so this is not worth an error.
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--bin-green-border)',
        background: 'var(--bin-green-bg)',
        borderRadius: 'var(--radius-lg)',
        padding: '16px 18px',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 'var(--text-h3)',
          fontWeight: 600,
          color: 'var(--bin-green-deep)',
        }}
      >
        {title}
      </h3>

      <p className="mt-1 text-sm" style={{ color: 'var(--bin-green-deep)' }}>
        Write this down or print it now. It is shown once and cannot be recovered — you would have
        to issue a new one.
      </p>

      <div className="mt-3 flex flex-col gap-1.5">
        {identifier && (
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-semibold" style={{ color: 'var(--bin-green-deep)' }}>
              Sign in with
            </span>
            <code
              data-numeric=""
              style={{ fontSize: fontPx(15), fontWeight: 700, color: 'var(--text-body)' }}
            >
              {identifier}
            </code>
          </div>
        )}
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold" style={{ color: 'var(--bin-green-deep)' }}>
            Password
          </span>
          <code
            data-numeric=""
            style={{ fontSize: fontPx(19), fontWeight: 800, letterSpacing: '0.04em' }}
          >
            {secret}
          </code>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="secondary" onClick={() => window.print()}>
          Print
        </Button>
        <Button onClick={onDismiss}>I have saved it</Button>
      </div>
    </div>
  );
}
